import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Constellation Components
import {
    Checkbox,
    FormHelp,
    IconCloseOutline,
    Input,
    Label,
    LabeledControl,
    Text,
} from '@zillow/constellation';

import {
    Col,
    HR,
    InputButtonGroup,
    Row,
} from '@zillow/zrs-comet';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';

// Styled Components
import {
    StyledBoldButton,
    StyledItemTagButton,
    StyledAddAmenityInputCol,
    StyledAvailableUnitsCol,
    StyledTagArrayDiv,
} from './StyledComponents';

const ENTER = 13;
const propTypes = {
    addTag: PropTypes.func.isRequired,
    removeTag: PropTypes.func.isRequired,
    tagsArray: PropTypes.array.isRequired,
    label: PropTypes.string,
};

const defaultProps = {
    label: '',
};

class AmenitiesTags extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            errorMsg: '',
            inputText: '',
        };

        this.handleInput = this.handleInput.bind(this);
        this.addTag = this.addTag.bind(this);
        this.handleKeyPress = this.handleKeyPress.bind(this);
        this.updateSelectUnitsHandler = this.updateSelectUnitsHandler.bind(this);
    }

    handleInput(e) {
        const inputText = e.target.value;

        if (inputText.length <= 100) {
            this.setState({
                errorMsg: '',
                inputText,
            });
        } else {
            this.setState({ errorMsg: 'Whoa, please keep this amenity to less than 100 characters.' });
        }
    }

    addTag() {
        const { dispatch } = this.props;

        let lowerCaseTagsArray;
        if (this.props.tagsArray.length > 0) {
            lowerCaseTagsArray = this.props.tagsArray.map(tag => tag.amenity.toLowerCase());
        } else {
            lowerCaseTagsArray = [];
        }

        const inputText = this.state.inputText.trim();

        if (inputText.length === 0) {
            AnalyticsConstants.GA_EVENTS.LISTING_INPUT_ERROR.action =
                `${AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME.AMENITIES}Error`;
            dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.LISTING_INPUT_ERROR));

            this.setState({
                errorMsg: 'Error: Please type in an amenity.',
            });
            return;
        }
        if (lowerCaseTagsArray.indexOf(inputText.toLowerCase()) > -1) {
            AnalyticsConstants.GA_EVENTS.LISTING_INPUT_ERROR.action =
                `${AnalyticsConstants.LISTING_COMPONENT_GROUP_NAME.AMENITIES}Error`;
            dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.LISTING_INPUT_ERROR));

            this.setState({
                errorMsg: 'Error: You\'ve already entered this tag.',
            });
            return;
        }

        this.setState({
            errorMsg: '',
            inputText: '',
        });
        this.props.addTag(inputText);
    }

    handleKeyPress(e) {
        this.setState({ errorMsg: '' });

        if (e.keyCode === ENTER) {
            e.preventDefault();
            this.addTag();
        }
    }

    updateSelectUnitsHandler() {
        this.props.updateSelectUnits();
    }

    render() {
        const { tagsArray } = this.props;
        let showSelectUnitsMessage = false;

        return (
            <div className="AmenitiesTags">
                <Row>
                    {this.props.label && (
                        <Col>
                            <Label>
                                { this.props.label }
                            </Label>
                        </Col>
                    )}
                    <StyledAddAmenityInputCol
                        md={6}
                    >
                        <InputButtonGroup>
                            <Input
                                aria-label="Enter an amenity"
                                disabled={this.props.disabled}
                                onChange={e => this.handleInput(e)}
                                onKeyDown={e => this.handleKeyPress(e)}
                                value={this.state.inputText}
                                data-test="amenities-add-input"
                                placeholder="Enter an amenity"
                                onBlur={this.addTag}
                            />
                            <StyledBoldButton
                                disabled={this.props.disabled}
                                fluid={true}
                                buttonType="primary"
                                data-test="amenities-add-button"
                            >
                                Add
                            </StyledBoldButton>
                        </InputButtonGroup>
                    </StyledAddAmenityInputCol>
                </Row>
                {this.props.showSelectUnits && (
                    <Row>
                        <StyledAvailableUnitsCol>
                            <LabeledControl
                                paddingY="xs"
                                controlId="Checkbox-available-units"
                                disabled={this.props.disabled}
                                label={<Label>Available in select units</Label>}
                                control={(
                                    <Checkbox
                                        checked={this.props.selectUnits}
                                        onChange={this.updateSelectUnitsHandler}
                                    />
                                )}
                            />
                        </StyledAvailableUnitsCol>
                    </Row>
                )}
                {this.state.errorMsg && (
                    <FormHelp error={this.state.errorMsg}>{this.state.errorMsg}</FormHelp>
                )}

                {tagsArray.length > 0 && (
                    <StyledTagArrayDiv>
                        <HR />

                        {tagsArray.map((tag, i) => {
                            let selectUnits = '';
                            if (tag.availableInSelectUnits) {
                                showSelectUnitsMessage = true;
                                selectUnits = ' *';
                            }

                            return (
                                <StyledItemTagButton
                                    marginTop={0}
                                    marginBottom="xs"
                                    marginLeft={0}
                                    marginRight="xs"
                                    padding="xs"
                                    key={`tag-${i}`}
                                    onClick={() => this.props.removeTag(i, tag.id)}
                                    data-test="amenities-delete-button"
                                    disabled={this.props.disabled}
                                >
                                    <Text>
                                        {tag.amenity + selectUnits}
                                        <Text paddingLeft="sm"><IconCloseOutline /></Text>
                                    </Text>
                                </StyledItemTagButton>
                            );
                        })}
                    </StyledTagArrayDiv>
                )}

                {showSelectUnitsMessage && (
                    <div className="select-units-message">
                        * Only in select units.
                    </div>
                )}
            </div>
        );
    }
}

AmenitiesTags.propTypes = propTypes;
AmenitiesTags.defaultProps = defaultProps;

export { AmenitiesTags };
export default connect()(AmenitiesTags);
